import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building, User, MapPin } from 'lucide-react';
import type { Supplier, SupplierFormData, SupplierContact } from '@/types/suppliers.types';

interface SupplierFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier?: Supplier;
  onSave: (supplier: SupplierFormData) => void;
}

export const SupplierFormModal: React.FC<SupplierFormModalProps> = ({
  open,
  onOpenChange,
  supplier,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState<SupplierFormData>({
    businessName: '',
    ruc: '',
    address: {
      street: '',
      city: '',
      region: '',
      postalCode: '',
      country: 'Perú'
    },
    primaryContact: {
      name: '',
      email: '',
      phone: '',
      position: ''
    },
    secondaryContacts: [],
    email: '',
    phone: '',
    website: '',
    creditDays: 30,
    isActive: true,
    notes: ''
  });
  
  const [contact, setContact] = useState<SupplierContact>({
    name: '',
    email: '',
    phone: '',
    position: ''
  });

  useEffect(() => {
    if (supplier) {
      setFormData({
        businessName: supplier.businessName,
        ruc: supplier.ruc,
        address: supplier.address,
        primaryContact: supplier.primaryContact,
        secondaryContacts: supplier.secondaryContacts || [],
        email: supplier.email || '',
        phone: supplier.phone || '',
        website: supplier.website || '',
        creditDays: supplier.creditDays || 30,
        isActive: supplier.isActive,
        notes: supplier.notes || ''
      });
      
      // Solo usar el contacto principal
      setContact(supplier.primaryContact);
    } else {
      // Reset form for new supplier
      const initialData: SupplierFormData = {
        businessName: '',
        ruc: '',
        address: {
          street: '',
          city: '',
          region: '',
          postalCode: '',
          country: 'Perú'
        },
        primaryContact: {
          name: '',
          email: '',
          phone: '',
          position: ''
        },
        secondaryContacts: [],
        email: '',
        phone: '',
        website: '',
        creditDays: 30,
        isActive: true,
        notes: ''
      };
      setFormData(initialData);
      setContact({
        name: '',
        email: '',
        phone: '',
        position: ''
      });
    }
    setActiveTab('basic');
  }, [supplier, open]);

  const handleSubmit = () => {
    // Validaciones básicas
    if (!formData.businessName.trim()) {
      alert('La razón social es requerida');
      return;
    }
    if (!formData.ruc.trim() || !/^\d{11}$/.test(formData.ruc)) {
      alert('El RUC debe tener exactamente 11 dígitos numéricos');
      return;
    }
    if (!formData.address.street.trim() || !formData.address.city.trim() || !formData.address.region.trim()) {
      alert('La dirección (calle, ciudad y región) es requerida');
      return;
    }

    const finalFormData: SupplierFormData = {
      ...formData,
      primaryContact: contact,
      secondaryContacts: [],
    };
    onSave(finalFormData);
    onOpenChange(false);
  };

  const updateContact = (field: keyof SupplierContact, value: string) => {
    setContact({ ...contact, [field]: value });
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Building className="w-5 h-5 mr-2" />
            {supplier ? 'Editar Proveedor' : 'Registrar Nuevo Proveedor'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic" className="flex items-center">
              <Building className="w-4 h-4 mr-2" />
              Básico
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              Contactos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-4">
            <Card className="p-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center">
                <Building className="w-5 h-5 mr-2" />
                Información General
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="businessName">Razón Social *</Label>
                  <Input
                    id="businessName"
                    value={formData.businessName}
                    onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                    placeholder="Ingrese la razón social"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ruc">RUC *</Label>
                  <Input
                    id="ruc"
                    value={formData.ruc}
                    onChange={(e) => setFormData({...formData, ruc: e.target.value})}
                    placeholder="20123456789"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="ventas@empresa.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+51 1 234-5678"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Sitio Web</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="www.empresa.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="creditDays">Días de Crédito</Label>
                  <Input
                    id="creditDays"
                    type="number"
                    min="0"
                    value={formData.creditDays}
                    onChange={(e) => setFormData({...formData, creditDays: parseInt(e.target.value) || 30})}
                    placeholder="30"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
                    />
                    <Label>{formData.isActive ? 'Activo' : 'Inactivo'}</Label>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <h3 className="font-semibold text-lg mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Dirección
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="street">Dirección *</Label>
                  <Input
                    id="street"
                    value={formData.address.street}
                    onChange={(e) => setFormData({
                      ...formData, 
                      address: {...formData.address, street: e.target.value}
                    })}
                    placeholder="Av. Principal 123"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ciudad *</Label>
                  <Input
                    id="city"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData, 
                      address: {...formData.address, city: e.target.value}
                    })}
                    placeholder="Lima"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">Región *</Label>
                  <Input
                    id="region"
                    value={formData.address.region}
                    onChange={(e) => setFormData({
                      ...formData, 
                      address: {...formData.address, region: e.target.value}
                    })}
                    placeholder="Lima"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Código Postal</Label>
                  <Input
                    id="postalCode"
                    value={formData.address.postalCode}
                    onChange={(e) => setFormData({
                      ...formData, 
                      address: {...formData.address, postalCode: e.target.value}
                    })}
                    placeholder="15001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">País</Label>
                  <Select 
                    value={formData.address.country} 
                    onValueChange={(value) => setFormData({
                      ...formData, 
                      address: {...formData.address, country: value}
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Perú">Perú</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Notas adicionales sobre el proveedor"
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="space-y-4">
            <Card className="p-4">
              <h3 className="font-semibold text-lg mb-4">Contacto del Proveedor</h3>

              <div className="border rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => updateContact('name', e.target.value)}
                      placeholder="Nombre del contacto"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cargo</Label>
                    <Input
                      value={contact.position || ''}
                      onChange={(e) => updateContact('position', e.target.value)}
                      placeholder="Gerente de Ventas"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={contact.email || ''}
                      onChange={(e) => updateContact('email', e.target.value)}
                      placeholder="contacto@empresa.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={contact.phone || ''}
                      onChange={(e) => updateContact('phone', e.target.value)}
                      placeholder="+51 987 654 321"
                    />
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>


        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="bg-rose-600 hover:bg-rose-700">
            {supplier ? 'Actualizar' : 'Registrar'} Proveedor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

